import { Capacitor } from '@capacitor/core';
import { Health, type HealthSample, type Workout as HKWorkout } from '@capgo/capacitor-health';

export interface HealthKitData {
  steps: number;
  restingHeartRate: number | null;
  sleepHours: number | null;
  activeCalories: number;
}

export interface HealthKitWorkoutInput {
  activityType:
    | 'traditional_strength_training'
    | 'functional_strength_training'
    | 'cardio';
  startDate: Date;
  endDate: Date;
  calories: number;
  /** Distance in meters (cardio only) */
  distance?: number;
}

export interface DailyMetric {
  /** ISO date YYYY-MM-DD (local) */
  date: string;
  value: number;
}

const HEALTHKIT_ASKED_KEY = 'healthkit_asked';

const isIOSNative = (): boolean =>
  Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform();

/** Local YYYY-MM-DD for a Date. */
const toLocalDateKey = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Wraps Capgo's @capgo/capacitor-health plugin (HealthKit on iOS).
 * Every call is wrapped in try/catch — HealthKit failures must never block the user.
 * All operations are no-ops on web/Android.
 */
export const useHealthKit = () => {
  const isAvailable = isIOSNative();

  const requestPermissions = async (): Promise<boolean> => {
    if (!isAvailable) return false;
    try {
      await Health.requestAuthorization({
        read: ['steps', 'restingHeartRate', 'sleep', 'calories', 'weight', 'heartRate', 'workouts'],
        write: ['weight', 'calories'],
      });
      try {
        localStorage.setItem(HEALTHKIT_ASKED_KEY, 'true');
      } catch {
        // ignore storage errors
      }

      // iOS quirk: requestAuthorization reports success even when the user
      // denied read access. Probe with a tiny aggregated steps query to detect
      // actual permission state.
      try {
        const now = new Date();
        const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        await Health.queryAggregated({
          dataType: 'steps',
          startDate: yesterday.toISOString(),
          endDate: now.toISOString(),
          bucket: 'day',
          aggregation: 'sum',
        });
        return true;
      } catch {
        return false;
      }
    } catch {
      return false;
    }
  };

  const getTodayData = async (): Promise<HealthKitData> => {
    const empty: HealthKitData = {
      steps: 0,
      restingHeartRate: null,
      sleepHours: null,
      activeCalories: 0,
    };
    if (!isAvailable) return empty;

    const endDate = new Date();
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);

    const startIso = startDate.toISOString();
    const endIso = endDate.toISOString();

    try {
      const [stepsRes, hrRes, sleepRes, caloriesRes] = await Promise.allSettled([
        Health.queryAggregated({
          dataType: 'steps',
          startDate: startIso,
          endDate: endIso,
          bucket: 'day',
          aggregation: 'sum',
        }),
        Health.readSamples({
          dataType: 'restingHeartRate',
          startDate: startIso,
          endDate: endIso,
          limit: 1,
          ascending: false,
        }),
        Health.readSamples({
          dataType: 'sleep',
          startDate: startIso,
          endDate: endIso,
          limit: 100,
          ascending: false,
        }),
        Health.queryAggregated({
          dataType: 'calories',
          startDate: startIso,
          endDate: endIso,
          bucket: 'day',
          aggregation: 'sum',
        }),
      ]);

      const totalSteps =
        stepsRes.status === 'fulfilled'
          ? (stepsRes.value.samples ?? []).reduce((sum, b) => sum + (b.value || 0), 0)
          : 0;

      const latestHR =
        hrRes.status === 'fulfilled'
          ? hrRes.value.samples?.[0]?.value ?? null
          : null;

      const sleepHoursRaw =
        sleepRes.status === 'fulfilled'
          ? (sleepRes.value.samples ?? []).reduce((sum, s: HealthSample) => {
              if (s.sleepState && s.sleepState !== 'asleep' && s.sleepState !== 'rem' &&
                  s.sleepState !== 'deep' && s.sleepState !== 'light') {
                return sum;
              }
              const start = new Date(s.startDate).getTime();
              const end = new Date(s.endDate).getTime();
              if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return sum;
              return sum + (end - start) / 3_600_000;
            }, 0)
          : 0;

      const totalCalories =
        caloriesRes.status === 'fulfilled'
          ? (caloriesRes.value.samples ?? []).reduce((sum, b) => sum + (b.value || 0), 0)
          : 0;

      return {
        steps: Math.round(totalSteps),
        restingHeartRate: latestHR != null ? Math.round(latestHR) : null,
        sleepHours: sleepHoursRaw > 0 ? Math.round(sleepHoursRaw * 10) / 10 : null,
        activeCalories: Math.round(totalCalories),
      };
    } catch {
      return empty;
    }
  };

  /**
   * Returns daily aggregates for a given metric across a date range.
   * Each entry corresponds to a calendar day; missing days are filled with 0.
   */
  const getDailyAggregated = async (
    dataType: 'steps' | 'calories',
    startDate: Date,
    endDate: Date,
  ): Promise<DailyMetric[]> => {
    if (!isAvailable) return [];
    try {
      const res = await Health.queryAggregated({
        dataType,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        bucket: 'day',
        aggregation: 'sum',
      });
      const byDate = new Map<string, number>();
      for (const sample of res.samples ?? []) {
        const key = toLocalDateKey(new Date(sample.startDate));
        byDate.set(key, (byDate.get(key) ?? 0) + (sample.value || 0));
      }
      // Fill missing days with 0 so the UI never has gaps.
      const out: DailyMetric[] = [];
      const cursor = new Date(startDate);
      cursor.setHours(0, 0, 0, 0);
      const stop = new Date(endDate);
      stop.setHours(0, 0, 0, 0);
      while (cursor <= stop) {
        const key = toLocalDateKey(cursor);
        out.push({ date: key, value: Math.round(byDate.get(key) ?? 0) });
        cursor.setDate(cursor.getDate() + 1);
      }
      return out;
    } catch {
      return [];
    }
  };

  const getStepsByDay = (start: Date, end: Date) => getDailyAggregated('steps', start, end);
  const getActiveEnergyByDay = (start: Date, end: Date) => getDailyAggregated('calories', start, end);

  const queryWorkoutsRange = async (start: Date, end: Date): Promise<HKWorkout[]> => {
    if (!isAvailable) return [];
    try {
      const res = await Health.queryWorkouts({
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        limit: 200,
      });
      return res.workouts ?? [];
    } catch {
      return [];
    }
  };

  const writeWorkout = async (workout: HealthKitWorkoutInput): Promise<boolean> => {
    if (!isAvailable) return false;
    try {
      const startIso = workout.startDate.toISOString();
      const endIso = workout.endDate.toISOString();
      const calories = Math.max(0, Math.round(workout.calories));

      await Health.saveSample({
        dataType: 'calories',
        value: calories,
        unit: 'kilocalorie',
        startDate: startIso,
        endDate: endIso,
        metadata: {
          source: 'mypocketcoach',
          activityType: workout.activityType,
        },
      });

      if (workout.activityType === 'cardio' && workout.distance && workout.distance > 0) {
        try {
          await Health.saveSample({
            dataType: 'distance',
            value: Math.round(workout.distance),
            unit: 'meter',
            startDate: startIso,
            endDate: endIso,
            metadata: { source: 'mypocketcoach' },
          });
        } catch {
          // Non-fatal
        }
      }
      return true;
    } catch {
      return false;
    }
  };

  const writeWeight = async (weightKg: number): Promise<void> => {
    if (!isAvailable) return;
    try {
      await Health.saveSample({
        dataType: 'weight',
        value: weightKg,
        unit: 'kilogram',
        startDate: new Date().toISOString(),
      });
    } catch {
      // Non-fatal
    }
  };

  return {
    isAvailable,
    requestPermissions,
    getTodayData,
    getStepsByDay,
    getActiveEnergyByDay,
    queryWorkoutsRange,
    writeWorkout,
    writeWeight,
  };
};
